import React, { useState, useEffect } from 'react';

const SubscriptionManagement = ({ user, onSave }) => {
  const [subscription, setSubscription] = useState({
    currentPlan: 'free',
    renewalDate: '',
    paymentMethod: null,
    billingHistory: []
  });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    // In a real app, you would fetch the user's subscription data from your backend
    // For now, we'll just use placeholder data
    setSubscription({
      currentPlan: 'free',
      renewalDate: '',
      paymentMethod: null,
      billingHistory: [
        // Example billing history would go here in a real app
      ]
    });
  }, [user]);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      interval: 'month',
      features: [
        'Basic data analysis',
        'Up to 5 sessions per month',
        'Standard insights',
        'Web access only'
      ],
      cta: 'Current Plan'
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 9.99,
      interval: 'month',
      features: [
        'Advanced data analysis',
        'Unlimited sessions',
        'AI-powered recommendations',
        'Web and mobile access',
        'Export data to PDF'
      ],
      cta: 'Upgrade'
    },
    {
      id: 'elite',
      name: 'Elite',
      price: 19.99,
      interval: 'month',
      features: [
        'Everything in Pro',
        'Club fitting recommendations',
        'Swing comparison with pros',
        'Priority support',
        'Share insights with coach'
      ],
      cta: 'Upgrade'
    }
  ];

  const handlePlanSelect = (plan) => {
    if (plan.id === subscription.currentPlan) {
      return;
    }
    
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setProcessingPayment(true);
      
      // In a real app, you would process the payment and update the subscription
      console.log('Processing payment for plan:', selectedPlan);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update subscription state
      setSubscription(prev => ({
        ...prev,
        currentPlan: selectedPlan.id,
        renewalDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        paymentMethod: {
          type: 'credit_card',
          last4: '4242',
          expiry: '12/25'
        }
      }));
      
      setProcessingPayment(false);
      setShowPaymentModal(false);
      onSave(true, `Successfully upgraded to ${selectedPlan.name} plan!`);
    } catch (error) {
      console.error('Error processing payment:', error);
      setProcessingPayment(false);
      onSave(false, 'Failed to process payment. Please try again.');
    }
  };

  const handleCancelSubscription = async () => {
    if (subscription.currentPlan === 'free') {
      return;
    }
    
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      return;
    }
    
    try {
      // In a real app, you would send a request to cancel the subscription
      console.log('Cancelling subscription');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update subscription state
      setSubscription(prev => ({
        ...prev,
        currentPlan: 'free',
        renewalDate: ''
      }));
      
      onSave(true, 'Your subscription has been cancelled. You will have access until the end of your billing period.');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      onSave(false, 'Failed to cancel subscription. Please try again.');
    }
  };

  return (
    <div className="profile-section">
      <h2>Subscription Management</h2>
      <p className="section-description">
        Manage your RangeDat subscription and billing information.
      </p>
      
      <div className="current-plan">
        <h3>Current Plan</h3>
        <div className="plan-details">
          <div className="plan-name">
            {plans.find(p => p.id === subscription.currentPlan)?.name || 'Free'} Plan
          </div>
          {subscription.renewalDate && (
            <div className="renewal-date">
              Renews on: {subscription.renewalDate}
            </div>
          )}
        </div>
      </div>
      
      <div className="payment-method">
        <h3>Payment Method</h3>
        {subscription.paymentMethod ? (
          <div className="payment-details">
            <div className="card-info">
              <span className="card-type">{subscription.paymentMethod.type}</span>
              <span className="card-number">•••• {subscription.paymentMethod.last4}</span>
              <span className="card-expiry">Expires {subscription.paymentMethod.expiry}</span>
            </div>
            <button className="btn-secondary">Update Payment Method</button>
          </div>
        ) : (
          <div className="no-payment-method">
            <p>No payment method on file.</p>
            {subscription.currentPlan !== 'free' && (
              <button className="btn-secondary">Add Payment Method</button>
            )}
          </div>
        )}
      </div>
      
      <div className="available-plans">
        <h3>Available Plans</h3>
        <div className="plans-grid">
          {plans.map(plan => (
            <div 
              key={plan.id} 
              className={`plan-card ${plan.id === subscription.currentPlan ? 'current-plan' : ''}`}
            >
              <div className="plan-header">
                <h4>{plan.name}</h4>
                <div className="plan-price">
                  ${plan.price}<span className="price-interval">/{plan.interval}</span>
                </div>
              </div>
              
              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
              
              <button 
                className={`btn-${plan.id === subscription.currentPlan ? 'disabled' : 'primary'}`}
                onClick={() => handlePlanSelect(plan)}
                disabled={plan.id === subscription.currentPlan}
              >
                {plan.id === subscription.currentPlan ? 'Current Plan' : plan.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
      
      {subscription.currentPlan !== 'free' && (
        <div className="cancel-subscription">
          <button 
            className="btn-text-danger"
            onClick={handleCancelSubscription}
          >
            Cancel Subscription
          </button>
          <p className="cancel-note">
            You will continue to have access to premium features until the end of your current billing period.
          </p>
        </div>
      )}
      
      {subscription.billingHistory.length > 0 && (
        <div className="billing-history">
          <h3>Billing History</h3>
          <table className="billing-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {subscription.billingHistory.map((item, index) => (
                <tr key={index}>
                  <td>{item.date}</td>
                  <td>{item.description}</td>
                  <td>${item.amount.toFixed(2)}</td>
                  <td>
                    <button className="btn-text">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="payment-modal">
            <div className="modal-header">
              <h3>Upgrade to {selectedPlan.name} Plan</h3>
              <button 
                className="close-modal"
                onClick={() => setShowPaymentModal(false)}
                disabled={processingPayment}
              >
                &times;
              </button>
            </div>
            
            <div className="modal-body">
              <div className="plan-summary">
                <p>You will be charged ${selectedPlan.price} per {selectedPlan.interval}.</p>
              </div>
              
              <form onSubmit={handlePaymentSubmit} className="payment-form">
                <div className="form-group">
                  <label htmlFor="cardName">Name on Card</label>
                  <input
                    type="text"
                    id="cardName"
                    placeholder="John Doe"
                    required
                    disabled={processingPayment}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="cardNumber">Card Number</label>
                  <input
                    type="text"
                    id="cardNumber"
                    placeholder="1234 5678 9012 3456"
                    required
                    disabled={processingPayment}
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cardExpiry">Expiration Date</label>
                    <input
                      type="text"
                      id="cardExpiry"
                      placeholder="MM/YY"
                      required
                      disabled={processingPayment}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="cardCvc">CVC</label>
                    <input
                      type="text"
                      id="cardCvc"
                      placeholder="123"
                      required
                      disabled={processingPayment}
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={() => setShowPaymentModal(false)}
                    disabled={processingPayment}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={processingPayment}
                  >
                    {processingPayment ? 'Processing...' : `Pay $${selectedPlan.price}`}
                  </button>
                </div>
              </form>
              
              <div className="payment-security">
                <p>
                  <span className="material-icons">lock</span>
                  Your payment information is secure. We use encryption to protect your data.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
